import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import { useCustomerDetail } from '../hooks/useCustomerDetail';
import CustomerViewPanel from './CustomerViewPanel';
import AvatarUploader from './AvatarUploader';
import { put, del } from '../../../api/client';
import { useAuthStore } from '../../../stores/authStore';
import { getTypeColor } from '../utils/typeColors';

interface FormState {
  fullName: string;
  phone: string;
  backupPhone: string;
  companyName: string;
  location: string;
  address: string;
  emergencyContact: string;
  emergencyPhone: string;
  introduction: string;
  customerTypeId: string;
  tagId: string;
}

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile } = useAuthStore();
  const { detail, customerTypes, availableTags, loading, error, refresh } = useCustomerDetail(id);

  // View / Edit mode — default view, switch to edit if ?edit=1
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === '1');

  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Track live avatar URL for edit mode (so it updates immediately after upload)
  const [liveAvatar, setLiveAvatar] = useState<string | null>(null);
  useEffect(() => {
    if (detail) setLiveAvatar(detail.avatar ?? null);
  }, [detail]);

  useEffect(() => {
    if (detail) {
      // 表单不收 email 与 idCard：这两项是本人注册账号时填的登录凭据，不由绑定方维护；
      // 且详情下发的 idCard 恒为掩码串，预填进输入框再原样提交会被后端当脱敏值回写拒 400
      setForm({
        fullName: detail.fullName ?? '',
        phone: detail.phone ?? '',
        backupPhone: detail.backupPhone ?? '',
        companyName: detail.companyName ?? '',
        location: detail.location ?? '',
        address: detail.address ?? '',
        emergencyContact: detail.emergencyContact ?? '',
        emergencyPhone: detail.emergencyPhone ?? '',
        introduction: detail.introduction ?? '',
        customerTypeId: detail.customerTypeId ?? '',
        tagId: detail.tags?.[0]?.id ?? '',
      });
    }
  }, [detail]);

  const setTag = (tagId: string) => {
    if (!form) return;
    setForm((prev) =>
      prev
        ? {
            ...prev,
            tagId: prev.tagId === tagId ? '' : tagId,
          }
        : prev
    );
  };

  const handleSave = async () => {
    if (!form || !detail || !id || !profile?.id) return;
    if (!form.fullName.trim()) {
      setSaveError('客户姓名不能为空');
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // 单次 PUT 一次性更新客户基础信息 + 类型 + 标签（后端内部会幂等 upsert userCustomer 及全量覆盖 tags）
      // 键整键省略而不是发 null：email / idCard 发 null 在后端是「显式清空」，省略才是「本次不改」
      await put(`/customers/${id}`, {
        fullName: form.fullName.trim(),
        phone: form.phone.trim() || null,
        backupPhone: form.backupPhone.trim() || null,
        companyName: form.companyName.trim() || null,
        location: form.location.trim() || null,
        address: form.address.trim() || null,
        emergencyContact: form.emergencyContact.trim() || null,
        emergencyPhone: form.emergencyPhone.trim() || null,
        introduction: form.introduction.trim() || null,
        customerTypeId: form.customerTypeId || null,
        tagId: form.tagId || null,
      });

      setSaveSuccess(true);
      refresh();
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !profile?.id) return;
    setDeleting(true);
    try {
      // 后端 DELETE /customers/:id 会先删 userCustomer（级联删 UserCustomerTag），
      // 若无其他用户关联且无订单关联则删 party
      await del(`/customers/${id}`);
      navigate('/customers');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : '删除失败');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const inputCls =
    'w-full px-3.5 py-3 sm:py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 bg-white text-gray-800 placeholder:text-gray-400 transition-colors';
  const labelCls = 'block text-xs font-medium text-gray-500 mb-1.5';

  // 名字直接取后端下发的 customerTypeName：`form.customerTypeId` 在这里是死逻辑——
  // CustomerViewPanel 只在 !isEditing 时渲染，编辑中的选择永远走不到这一行。
  // 而拿 customerTypeId 去目录里现查会造出第二份事实源：绑定的类型一旦被删（目录只下发未删的），
  // 详情页就整块不显示，列表卡片却仍显示名字。Flutter 与 hmos 都是直读这个字段。
  const customerTypeName = detail?.customerTypeName ?? null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafa]">
        <Navbar />
        <div className="max-w-[900px] mx-auto px-6 py-10">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-100 rounded w-32" />
            <div className="h-40 bg-white rounded-xl border border-gray-100" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !detail || !form) {
    return (
      <div className="min-h-screen bg-[#f8fafa]">
        <Navbar />
        <div className="max-w-[900px] mx-auto px-6 py-10 text-center">
          <p className="text-gray-400">{error ?? '客户不存在'}</p>
          <button
            type="button"
            onClick={() => navigate('/customers')}
            className="mt-4 px-4 py-2 text-sm text-teal-600 border border-teal-200 rounded-lg cursor-pointer hover:bg-teal-50"
          >
            返回客户列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafa]">
      <Navbar />

      <div className="max-w-[900px] mx-auto px-4 md:px-6 py-5 md:py-8 pb-24 md:pb-8">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-8">
          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-white border border-transparent hover:border-gray-200 rounded-lg transition-all cursor-pointer shrink-0"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg md:text-xl font-bold text-gray-900">客户详细信息</h1>
                {/* Mode badge */}
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                  isEditing
                    ? 'bg-amber-50 text-amber-600 border border-amber-200'
                    : 'bg-teal-50 text-teal-600 border border-teal-100'
                }`}>
                  {isEditing ? '编辑中' : '查看'}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                创建于 {detail.createdAt ? new Date(detail.createdAt).toLocaleDateString('zh-CN') : '--'}
              </p>
            </div>
          </div>

          {/* Right: action buttons — always visible */}
          <div className="flex items-center gap-2 md:gap-3">
            {!isEditing ? (
              /* View mode */
              <>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-400 border border-red-100 bg-white hover:bg-red-50 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-delete-bin-line" />
                  </div>
                  <span className="hidden sm:inline">删除客户</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-edit-line" />
                  </div>
                  <span className="hidden sm:inline">编辑信息</span>
                </button>
              </>
            ) : (
              /* Edit mode */
              <>
                <button
                  type="button"
                  onClick={() => { setIsEditing(false); refresh(); }}
                  className="px-3 py-2 text-sm text-gray-500 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 disabled:opacity-60 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={saving ? 'ri-loader-4-line animate-spin' : 'ri-save-line'} />
                  </div>
                  <span className="hidden sm:inline">{saving ? '保存中...' : '保存修改'}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Success / Error messages */}
        {saveSuccess && (
          <div className="flex items-center gap-2 p-3 bg-teal-50 border border-teal-100 rounded-xl text-sm text-teal-600 mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-checkbox-circle-line" />
            </div>
            客户信息已保存
          </div>
        )}
        {saveError && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-500 mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-error-warning-line" />
            </div>
            {saveError}
          </div>
        )}

        {/* View mode */}
        {!isEditing && (
          <CustomerViewPanel
            detail={detail}
            customerTypeName={customerTypeName}
          />
        )}

        {/* Edit mode */}
        {isEditing && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {/* Left: Main Info */}
            <div className="md:col-span-2 space-y-4 md:space-y-5">

              {/* Avatar upload card */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-4 md:mb-5 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center bg-teal-50 rounded">
                    <i className="ri-image-line text-teal-500 text-xs" />
                  </div>
                  客户头像
                </h2>
                <AvatarUploader
                  customerId={id!}
                  name={form.fullName || detail.fullName}
                  avatarUrl={liveAvatar}
                  onAvatarChange={(url) => setLiveAvatar(url)}
                />
              </div>

              {/* Basic Info Card */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-4 md:mb-5 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center bg-teal-50 rounded">
                    <i className="ri-user-line text-teal-500 text-xs" />
                  </div>
                  基本信息
                </h2>

                <div className="space-y-3 md:space-y-4">
                  <div>
                    <label className={labelCls}>
                      客户姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      value={form.fullName}
                      onChange={(e) => setForm((p) => p ? { ...p, fullName: e.target.value } : p)}
                      className={inputCls}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>手机号码</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm((p) => p ? { ...p, phone: e.target.value } : p)}
                        placeholder="主要联系电话"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>备用号码</label>
                      <input
                        type="tel"
                        value={form.backupPhone}
                        onChange={(e) => setForm((p) => p ? { ...p, backupPhone: e.target.value } : p)}
                        placeholder="备用联系电话"
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>公司/单位</label>
                    <input
                      value={form.companyName}
                      onChange={(e) => setForm((p) => p ? { ...p, companyName: e.target.value } : p)}
                      placeholder="所在公司或单位"
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>

              {/* Location Card */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-4 md:mb-5 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center bg-teal-50 rounded">
                    <i className="ri-map-pin-line text-teal-500 text-xs" />
                  </div>
                  地址信息
                </h2>
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <label className={labelCls}>所在城市</label>
                    <input
                      value={form.location}
                      onChange={(e) => setForm((p) => p ? { ...p, location: e.target.value } : p)}
                      placeholder="如：北京市"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>详细地址</label>
                    <input
                      value={form.address}
                      onChange={(e) => setForm((p) => p ? { ...p, address: e.target.value } : p)}
                      placeholder="街道、楼号等详细地址"
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact Card */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-4 md:mb-5 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center bg-teal-50 rounded">
                    <i className="ri-shield-user-line text-teal-500 text-xs" />
                  </div>
                  紧急联系人
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>联系人姓名</label>
                    <input
                      value={form.emergencyContact}
                      onChange={(e) => setForm((p) => p ? { ...p, emergencyContact: e.target.value } : p)}
                      placeholder="紧急联系人"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>联系人电话</label>
                    <input
                      type="tel"
                      value={form.emergencyPhone}
                      onChange={(e) => setForm((p) => p ? { ...p, emergencyPhone: e.target.value } : p)}
                      placeholder="紧急联系电话"
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-4 md:mb-5 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center bg-teal-50 rounded">
                    <i className="ri-file-text-line text-teal-500 text-xs" />
                  </div>
                  备注信息
                </h2>
                <textarea
                  value={form.introduction}
                  onChange={(e) => setForm((p) => p ? { ...p, introduction: e.target.value } : p)}
                  placeholder="关于这位客户的备注、偏好、特殊说明..."
                  rows={4}
                  maxLength={500}
                  className={`${inputCls} resize-none`}
                />
                <p className="text-xs text-gray-300 text-right mt-1">
                  {form.introduction.length}/500
                </p>
              </div>
            </div>

            {/* Right: Sidebar */}
            <div className="md:col-span-1 space-y-3 md:space-y-4">
              {/* Customer Type */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">客户类型</h3>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setForm((p) => p ? { ...p, customerTypeId: '' } : p)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer ${
                      !form.customerTypeId
                        ? 'bg-teal-50 text-teal-600 border border-teal-200'
                        : 'text-gray-500 hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    不设置
                  </button>
                  {customerTypes.map((t) => {
                    const selected = form.customerTypeId === t.id;
                    const color = getTypeColor(t.name);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setForm((p) => p ? { ...p, customerTypeId: t.id } : p)}
                        className="w-full text-left px-3 py-2 text-sm rounded-lg transition-all cursor-pointer border"
                        style={
                          selected
                            ? {
                                backgroundColor: color.bg,
                                color: color.text,
                                borderColor: color.border,
                              }
                            : {
                                backgroundColor: 'transparent',
                                color: '#374151',
                                borderColor: 'transparent',
                              }
                        }
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{ backgroundColor: selected ? color.dot : '#d1d5db' }}
                            />
                            <span className="truncate">{t.name}</span>
                          </div>
                          {selected && (
                            <div className="w-4 h-4 flex items-center justify-center shrink-0">
                              <i className="ri-check-line text-xs" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tags */}
              {availableTags.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">客户标签</h3>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map((tag) => {
                      const selected = form.tagId === tag.id;
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => setTag(tag.id)}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer"
                          style={
                            selected
                              ? {
                                  backgroundColor: `${tag.color ?? '#14b8a6'}25`,
                                  color: tag.color ?? '#14b8a6',
                                  border: `1.5px solid ${tag.color ?? '#14b8a6'}`,
                                }
                              : {
                                  backgroundColor: '#f9fafb',
                                  color: '#9ca3af',
                                  border: '1.5px solid #e5e7eb',
                                }
                          }
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: selected ? (tag.color ?? '#14b8a6') : '#d1d5db' }}
                          />
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quick info card */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">快速信息</h3>
                <div className="space-y-3">
                  {detail.phone && (
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg shrink-0">
                        <i className="ri-phone-line text-teal-500 text-xs" />
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{detail.phone}</span>
                    </div>
                  )}
                  {detail.email && (
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg shrink-0">
                        <i className="ri-mail-line text-teal-500 text-xs" />
                      </div>
                      <span className="text-sm text-gray-700 truncate">{detail.email}</span>
                    </div>
                  )}
                  {detail.location && (
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg shrink-0">
                        <i className="ri-map-pin-line text-teal-500 text-xs" />
                      </div>
                      <span className="text-sm text-gray-700">{detail.location}</span>
                    </div>
                  )}
                  {!detail.phone && !detail.email && !detail.location && (
                    <p className="text-xs text-gray-300 text-center py-2">暂无联系信息</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-400 text-xl" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 text-center mb-2">确认删除客户</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              删除「{detail.fullName}」后无法恢复，该客户的所有记录将被清除。
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 text-sm text-gray-500 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-red-400 hover:bg-red-500 disabled:opacity-60 rounded-xl cursor-pointer transition-colors whitespace-nowrap"
              >
                {deleting ? '删除中...' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
